<script setup lang="ts">
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

interface TextRevealProps {
  delay?: number
  text?: string | undefined
  linesClass?: string | undefined
}

const props = withDefaults(defineProps<TextRevealProps>(), {
  delay: 0,
  text: undefined,
  linesClass: undefined
})

const isMounted = ref(false)
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null
const tween = ref<gsap.core.Tween | null>(null)
const split = ref<SplitText | null>(null)

onMounted(() => {
  gsap.registerPlugin(SplitText, ScrollTrigger)
  isMounted.value = true

  if (ctxRef.value) {
    ctx = gsap.context(() => {
      split.value = new SplitText(ctxRef.value, {
        type: 'lines',
        tag: 'span',
        linesClass: props.linesClass + ' inline-block'
      })

      new SplitText(ctxRef.value, {
        type: 'lines',
        tag: 'span',
        linesClass: 'overflow-hidden inline-block'
      })

      gsap.set(split.value.lines, { translateY: '100%' })
      tween.value = gsap.to(split.value.lines, {
        translateY: '0%',
        delay: props.delay,
        duration: 1.3,
        ease: 'power4.out',
        paused: true,
        stagger: 0.1
      })

      ScrollTrigger.batch(ctxRef.value, {
        start: 'top bottom-=70px',
        onEnter: () => {
          tween.value?.play()
        }
      })
      ScrollTrigger.refresh()
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <span
    v-if="props.text"
    ref="ctxRef"
    :class="isMounted ? 'visible' : 'invisible'"
    class="inline-block"
    v-html="props.text"
  />
  <span v-else ref="ctxRef" class="inline-block" :class="isMounted ? 'visible' : 'invisible'">
    <slot />
  </span>
</template>
